/* eslint-disable max-len */
import type { TemplateCategory, PatientInfoField } from './types'
import { FormTemplateStatus } from '@dataTypes/forms.type'

/** Text-appropriate patient fields for Input Field and Text Area. No Date of Birth; use Date Field. */
export const INPUT_FIELD_AUTOFILL_OPTIONS: Array<{
  value: PatientInfoField | null
  label: string
}> = [
  { value: null, label: 'None' },
  { value: 'firstName', label: 'First name' },
  { value: 'middleName', label: 'Middle name' },
  { value: 'lastName', label: 'Last name' },
  { value: 'preferredName', label: 'Preferred Name' },
  { value: 'title', label: 'Title' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'address', label: 'Address' },
  { value: 'city', label: 'City' },
  { value: 'state', label: 'State' },
  { value: 'zip', label: 'ZIP Code' },
]

/** Date-appropriate patient fields for Date Field. */
export const DATE_FIELD_AUTOFILL_OPTIONS: Array<{
  value: PatientInfoField | null
  label: string
}> = [
  { value: null, label: 'None' },
  { value: 'dateOfBirth', label: 'Date of Birth' },
]

/** For Radio Group. Gender is one possible auto-fill source. */
export const GENDER_SELECTION_AUTOFILL_OPTIONS: Array<{
  value: PatientInfoField | null
  label: string
}> = [
  { value: null, label: 'None' },
  { value: 'gender', label: 'Gender' },
]

/** Set to true to show Attachments and Dentist/Admin Signature in the form component sidebar. */
export const SHOW_ATTACHMENTS_AND_ADMIN_SIGNATURE_COMPONENTS = false

/** Sentence-case label for sidebar and other UI (internal `type` values stay Title Case). */
export const formatFormComponentDisplayLabel = (
  componentType: string,
): string => {
  const spaceIndex = componentType.indexOf(' ')
  if (spaceIndex === -1) {
    return (
      componentType.charAt(0).toUpperCase() +
      componentType.slice(1).toLowerCase()
    )
  }
  return (
    componentType.slice(0, spaceIndex) +
    componentType.slice(spaceIndex).toLowerCase()
  )
}

export const FORM_COMPONENTS = [
  'Group Fields',
  'Form Row',
  'Input Field',
  'Text Area',
  'Date Field',
  'Checkbox',
  'Checkbox Group',
  'Dropdown',
  'Radio Group',
  'Rich Text',
  'Attachments',
  'Signature Field',
  'Dentist/Admin Signature',
] as const

/** Tooltips for form components – when to use and how they behave */
export const COMPONENT_TOOLTIPS: Record<
  (typeof FORM_COMPONENTS)[number],
  string
> = {
  'Group Fields':
    'A section with a title (e.g. "Personal Information"). Add fields inside; they will wrap with a fixed layout. Use Form Row inside if you want fields side-by-side with equal width.',
  'Form Row':
    'Fields dropped inside sit in one row and share width equally (e.g. First name | Last name). Use when you want side-by-side fields with equal columns. Without Form Row, fields use the default layout.',
  'Input Field': 'Single-line text input. Use for names, phone, email, etc.',
  'Text Area':
    'Multi-line text the patient fills in. Use for notes, comments, or longer answers.',
  'Date Field': 'Date picker. Use for birth date, appointment date, etc.',
  Checkbox: 'Single yes/no checkbox.',
  'Checkbox Group':
    'Several checkboxes. The patient can select more than one. Edit the choices in field settings.',
  Dropdown:
    'Single choice from a dropdown list. Edit the choices in field settings.',
  'Radio Group':
    'Single choice shown as radio buttons. Edit the choices in field settings. For gender, use Male, Female, and Other and auto-fill from patient gender.',
  'Rich Text':
    'Standalone paragraph block (no title). Creator-authored text shown to patients; cannot be placed inside a group.',
  Attachments: 'File upload. Use for documents, images, or other files.',
  'Signature Field': 'Patient or signer signature capture.',
  'Dentist/Admin Signature':
    'Signature field for dentist or staff (e.g. approval, attestation).',
}

export const TEMPLATE_CATEGORIES: TemplateCategory[] = [
  {
    title: 'Patient Information',
    createButtonText: 'Create Patient Related Forms',
    items: [
      {
        name: 'New Patient Form',
        status: FormTemplateStatus.Pending,
        icon: 'form',
      },
      {
        name: 'Returning Patient Form',
        status: FormTemplateStatus.Pending,
        icon: 'form',
      },
      {
        name: 'Insurance Information Form',
        status: FormTemplateStatus.Pending,
        icon: 'form',
      },
    ],
  },
  {
    title: 'Consent Forms',
    createButtonText: 'Create Consent Forms',
    items: [
      {
        name: 'General Consent Form',
        status: FormTemplateStatus.Pending,
        icon: 'form',
      },
      {
        name: 'HIPAA/Privacy Acknowledgement',
        status: FormTemplateStatus.Pending,
        icon: 'form',
      },
      {
        name: 'Appointment Cancellation Policy',
        status: FormTemplateStatus.Pending,
        icon: 'form',
      },
    ],
  },
  {
    title: 'Others',
    createButtonText: 'Create Other Types of Forms',
    items: [],
  },
]
